import { gql, useMutation } from '@apollo/client';
import { convertFromRaw } from 'draft-js';
import * as React from 'react';

import { analyzeContent, post_created } from '@/analytics';
import { UpdateBlogDataInput_V2 } from '@/graphql-types/globalTypes';
import { logError } from '@/imports/logging/ClientLogger';

import {
  useUpdateBlog_updateBlogV2,
  useUpdateBlog_updateBlogV2Variables,
} from './graphql-types/useUpdateBlog_updateBlogV2';

const updateBlogWithPostAST = gql`
  mutation useUpdateBlog_updateBlogV2($input: UpdateBlogInput_V2!) {
    updateBlogV2(input: $input) {
      data {
        _id
        scheduledPublishDate
        isAnnouncementScheduled
        organization {
          _id
          name
        }
        group {
          _id
          name
          isGlobalSingleton
        }
        published
        title
        content
        bannerImage {
          _id
          imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
        }
        tags {
          _id
          name
        }
      }
    }
  }
`;

type BlogMutationOptions = {
  publish: boolean;
  isAnnouncement: boolean;
  alreadyPublished: boolean;
};

type UpdateBlogOptions = {
  postId: string;
  options: BlogMutationOptions;
  blog: UpdateBlogDataInput_V2 & { organizationId?: string };
};

export const useUpdateBlog = (): {
  updateBlog: (options: UpdateBlogOptions) => Promise<boolean>;
} => {
  const [updateFunc] = useMutation<
    useUpdateBlog_updateBlogV2,
    useUpdateBlog_updateBlogV2Variables
  >(updateBlogWithPostAST, {
    refetchQueries: ['DraftsPage_GetMyDraftPosts', 'AccountMenu_Query'],
  });

  const updateBlog = React.useCallback(
    async (options: UpdateBlogOptions) => {
      const { publish, isAnnouncement, alreadyPublished } = options.options;

      const blog = options.blog;

      try {
        const result = await updateFunc({
          variables: {
            input: {
              data: {
                ...blog,
                published: publish,
              },
              blogPostId: options.postId,
              isAnnouncement: isAnnouncement,
            },
          },
        });

        if (!alreadyPublished && publish) {
          const resultBlogPost = result.data?.updateBlogV2.data;
          const postedIn = !!resultBlogPost?.group?.isGlobalSingleton
            ? 'home'
            : !!blog?.organizationId
            ? 'organization'
            : 'group';

          const bodyText = `${blog.title}\n${convertFromRaw(
            blog.content
          ).getPlainText()}`;
          const contentAnalysis = analyzeContent(bodyText);

          post_created({
            entity_type: 'post',
            entity_id: options.postId,
            post_type: 'article',
            content_type: 'article',
            post_is_announcement: !!isAnnouncement,
            location_posted_in: postedIn,
            location_group_id: !blog?.organizationId
              ? resultBlogPost?.group?._id
              : undefined,
            location_organization_id: blog?.organizationId,
            content_text: contentAnalysis.contentText,
            content_length: contentAnalysis.contentLength,
            content_word_count: contentAnalysis.wordCount,
            content_has_question_mark: contentAnalysis.hasQuestionMark,
            content_has_links: contentAnalysis.hasLinks,
            content_link_count: contentAnalysis.linkCount,
            content_mention_count: contentAnalysis.mentionCount,
            content_hashtag_count: contentAnalysis.hashtagCount,
            content_hashtags: contentAnalysis.hashtags,
            content_has_tags: !!blog.tagIds && blog.tagIds.length > 0,
            content_has_media: !!blog.blogBannerImageId,
          });
        }

        return true;
      } catch (err) {
        logError(err);
        return false;
      }
    },
    [updateFunc]
  );

  return {
    updateBlog,
  };
};
